import React, { useEffect, useMemo, useRef, useState } from "react";
import { fetchTopPlates, fetchLatestMedia } from "../lib/topPlatesApi.js";
import ExportMenu from "../components/ExportMenu.jsx";
import ImagesPanel from "../features/sightings/ImagesPanel.jsx";
import DaypartChips from "../features/stats/DaypartChips.jsx";
import CameraChips from "../features/stats/CameraChips.jsx";

/**
 * Top Plates — mirrors Sightings layout/behavior.
 */

const ui = {
  row: { display: "flex", alignItems: "center", gap: 10, rowGap: 10, flexWrap: "wrap" },
  group: { display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" },
  label: { opacity: 0.9, fontSize: 14 },
  btn: {
    height: 32,
    padding: "0 12px",
    borderRadius: 6,
    background: "#14304f",
    border: "1px solid #2a4d77",
    color: "#dbe7ff",
    cursor: "pointer",
    textDecoration: "none",
    display: "inline-flex",
    alignItems: "center",
  },
  btnActive: {
    height: 32,
    padding: "0 12px",
    borderRadius: 6,
    background: "#1c2f4f",
    border: "1px solid #2b63b6",
    color: "#e7f1ff",
    cursor: "pointer",
    boxShadow: "inset 0 0 0 1px #3b82f6",
  },
  input: {
    height: 32,
    background: "#0d1420",
    border: "1px solid #1f2a39",
    color: "#dbe7ff",
    borderRadius: 6,
    padding: "0 10px",
  },
};

// camera key mapping used across app
const normalizeCamKey = (k) => {
  const s = String(k || "").toLowerCase();
  if (s === "cam41" || s === "41") return "41";
  if (s === "chel" || s === "cheltenham") return "cheltenham";
  if (s === "pool") return "pool";
  return s;
};

// export helpers
function downloadBlob(filename, mime, data) {
  const blob = new Blob([data], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
function toCsv(rows) {
  const header = ["Plate", "41", "Cheltenham", "Pool", "Total"];
  const lines = [header.join(",")];
  rows.forEach((r) => {
    lines.push(
      [r.plate ?? "", r["41"] ?? 0, r.cheltenham ?? 0, r.pool ?? 0, r.total ?? 0]
        .map((v) => String(v).replace(/"/g, '""'))
        .map((v) => (v.includes(",") ? `"${v}"` : v))
        .join(",")
    );
  });
  return lines.join("\n");
}
function toExcelHtml(rows) {
  const head = "<tr><th>Plate</th><th>41</th><th>Cheltenham</th><th>Pool</th><th>Total</th></tr>";
  const body = rows
    .map(
      (r) =>
        `<tr><td>${r.plate ?? ""}</td><td>${r["41"] ?? 0}</td><td>${r.cheltenham ?? 0}</td><td>${
          r.pool ?? 0
        }</td><td>${r.total ?? 0}</td></tr>`
    )
    .join("");
  return `<!DOCTYPE html><meta charset="utf-8"><table>${head}${body}</table>`;
}
async function exportPdf(rows) {
  try {
    const { default: jsPDF } = await import("jspdf");
    const auto = await import("jspdf-autotable");
    const doc = new jsPDF();
    const body = rows.map((r) => [
      r.plate ?? "",
      r["41"] ?? 0,
      r.cheltenham ?? 0,
      r.pool ?? 0,
      r.total ?? 0,
    ]);
    auto.default(doc, {
      head: [["Plate", "41", "Cheltenham", "Pool", "Total"]],
      body,
      styles: { fontSize: 10, cellPadding: 2 },
      headStyles: { fillColor: [20, 24, 31] },
    });
    doc.save("top-plates.pdf");
  } catch {
    window.print();
  }
}
function buildExportPayload(rows) {
  return {
    title: "Top Plates",
    filename: "top-plates",
    headers: ["Plate", "41", "Cheltenham", "Pool", "Total"],
    rows: rows.map((r) => ({
      Plate: r.plate ?? "",
      "41": r["41"] ?? 0,
      Cheltenham: r.cheltenham ?? 0,
      Pool: r.pool ?? 0,
      Total: r.total ?? 0,
    })),
  };
}

// numeric extraction
const take = (rec, variants) => {
  for (const k of variants) if (rec?.[k] != null) return Number(rec[k]) || 0;
  return 0;
};

export default function TopPlates() {
  // filters
  const [period, setPeriod] = useState("");
  const [fromTs, setFromTs] = useState("");
  const [toTs, setToTs] = useState("");

  const [bucket, setBucket] = useState(""); // morning | afternoon | evening | overnight
  const [sunMode, setSunMode] = useState(null); // 1 (Dusk–Dawn) | 0 (Dawn–Dusk) | null

  // cameras
  const [cams, setCams] = useState({ cam41: true, chel: true, pool: true });

  // paging
  const [limitInput, setLimitInput] = useState(20);
  const [limit, setLimit] = useState(20);
  const [offset, setOffset] = useState(0);

  // data
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [totalGroups, setTotalGroups] = useState(0);

  // images
  const [selectedPlate, setSelectedPlate] = useState("");
  const [latestMedia, setLatestMedia] = useState(null);
  const [mediaLoading, setMediaLoading] = useState(false);
  const imagesRef = useRef(null);
  const reqIdRef = useRef(0);

  const timeChips = [
    { key: "today", label: "Today" },
    { key: "this_week", label: "This Week" },
    { key: "last_week", label: "Last Week" },
    { key: "this_month", label: "This Month" },
    { key: "last_month", label: "Last Month" },
    { key: "this_year", label: "This Year" },
    { key: "last_year", label: "Last Year" },
  ];

  // selected cameras -> ["41","cheltenham","pool"]
  const camsArray = useMemo(
    () =>
      Object.entries(cams)
        .filter(([, v]) => !!v)
        .map(([k]) => normalizeCamKey(k)),
    [cams]
  );

  // -------- table query (Top Plates) --------
  const apiQuery = useMemo(() => {
    const q = { limit, offset, cameras: camsArray };

    // time window
    if (period) {
      q.period = period;
    } else if (fromTs || toTs) {
      q.period = "custom";
      if (fromTs) q.start = fromTs;
      if (toTs) q.end = toTs;
    } else {
      q.period = "anytime";
    }

    // DAYPART: this endpoint expects BUCKETS for sunlight chips
    if (sunMode === 1 || sunMode === "1") {
      q.buckets = "dusk-dawn";
    } else if (sunMode === 0 || sunMode === "0") {
      q.buckets = "dawn-dusk";
    } else if (bucket) {
      q.buckets = bucket; // morning | afternoon | evening | overnight
    }

    return q;
  }, [limit, offset, camsArray, period, fromTs, toTs, bucket, sunMode]);

  // load table
  useEffect(() => {
    let ignore = false;
    setLoading(true);
    (async () => {
      try {
        const data = await fetchTopPlates(apiQuery);
        if (ignore) return;

        const normalized = (data?.rows || []).map((r) => ({
          plate: r.plate,
          "41": take(r, ["41", "cam41", "c41"]),
          cheltenham: take(r, ["Cheltenham", "cheltenham", "camChel", "cam_cheltenham"]),
          pool: take(r, ["Pool", "pool", "camPool", "cam_pool"]),
          total: Number(r.total ?? r.Total ?? 0) || 0,
        }));
        setRows(normalized);

        const tg =
          data?.meta?.total_groups ??
          data?.meta?.total ??
          (Array.isArray(data?.rows) ? data.rows.length : 0);
        setTotalGroups(Number.isFinite(tg) ? tg : 0);
      } catch {
        if (!ignore) {
          setRows([]);
          setTotalGroups(0);
        }
      } finally {
        if (!ignore) setLoading(false);
      }
    })();
    return () => {
      ignore = true;
    };
  }, [apiQuery]);

  // -------- image fetch (media/latest) --------
  async function fetchImagesForPlate(plate) {
    setMediaLoading(true);
    const myReq = ++reqIdRef.current;

    try {
      const args = { plate, exact: true };

      // time
      if (period) {
        args.period = period;
      } else if (fromTs || toTs) {
        args.period = "custom";
        if (fromTs) args.start = fromTs;
        if (toTs) args.end = toTs;
      }
      // daypart:
      //  - use is_night for sunlight chips
      //  - use clock buckets for morning/afternoon/evening/overnight
      if (sunMode === 1 || sunMode === "1") {
        args.is_night = 1;
      } else if (sunMode === 0 || sunMode === "0") {
        args.is_night = 0;
      } else if (bucket) {
        args.buckets = [bucket];
      }

      // camera: restrict only when exactly one selected
      if (camsArray.length === 1) {
        args.camera = camsArray[0];
      }

      console.debug("[TopPlates] /api/media/latest args →", args);
      let m = await fetchLatestMedia(args);

      // retry without camera if nothing found
      if (!m?.found && args.camera) {
        const { camera, ...noCam } = args;
        console.debug("[TopPlates] retry without camera →", noCam);
        m = await fetchLatestMedia(noCam);
      }

      if (myReq === reqIdRef.current) {
        setLatestMedia(m?.found ? m : null);
      }
    } catch (e) {
      console.debug("[TopPlates] media/latest error", e);
      if (myReq === reqIdRef.current) setLatestMedia(null);
    } finally {
      if (myReq === reqIdRef.current) setMediaLoading(false);
    }
  }

  // keep images in sync when filters change for the selected plate
  useEffect(() => {
    if (!selectedPlate) {
      setLatestMedia(null);
      return;
    }
    fetchImagesForPlate(selectedPlate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlate, camsArray.join(","), period, fromTs, toTs, bucket, sunMode]);

  // Sightings deep-link
  const sightingsHref = useMemo(() => {
    if (!selectedPlate) return "";
    const p = new URLSearchParams();
    p.set("plate", selectedPlate);
    p.set("exact", "true");

    if (period) {
      p.set("period", period);
    } else if (fromTs || toTs) {
      p.set("period", "custom");
      if (fromTs) p.set("start", fromTs);
      if (toTs) p.set("end", toTs);
    } else {
      p.set("period", "this_year");
    }

    // send both for maximum compatibility on the Sightings page
    if (sunMode === 1 || sunMode === "1") {
      p.set("is_night", "1");
      p.set("buckets", "dusk-dawn");
    } else if (sunMode === 0 || sunMode === "0") {
      p.set("is_night", "0");
      p.set("buckets", "dawn-dusk");
    } else if (bucket) {
      p.set("buckets", bucket);
    }

    if (camsArray.length) p.set("cameras", camsArray.join(","));
    return `/dev/sightings?${p.toString()}`;
  }, [selectedPlate, period, fromTs, toTs, bucket, sunMode, camsArray]);

  // handlers
  const applyDates = () => {
    setPeriod(""); // switch into custom via from/to
    setOffset(0);
  };
  const applyLimit = () => {
    setOffset(0);
    setLimit(Math.max(1, Number(limitInput) || 20));
  };

  const pageFirst = totalGroups === 0 ? 0 : offset + 1;
  const pageLast = Math.min(offset + rows.length, totalGroups);

  return (
    <div style={{ padding: 24, color: "#dbe7ff" }}>
      <h1 style={{ fontSize: 26, fontWeight: 700, marginBottom: 16 }}>Top Plates</h1>

      {/* Row 1: Daypart chips */}
      <div style={ui.row}>
        <span style={ui.label}>Daypart:</span>
        <DaypartChips
          bucket={bucket}
          setBucket={(b) => {
            setBucket(b);
            setSunMode(null);
            setOffset(0);
          }}
          sunMode={sunMode}
          setSunMode={(s) => {
            setSunMode(s);
            if (s !== null) setBucket("");
            setOffset(0);
          }}
        />
      </div>

      {/* Row 2: Quick ranges + Custom range */}
      <div style={{ ...ui.row, marginTop: 6 }}>
        <div style={ui.group}>
          <span style={ui.label}>Quick ranges:</span>
          {timeChips.map((t) => (
            <button
              key={t.key}
              onClick={() => {
                setPeriod(t.key);
                setOffset(0);
              }}
              style={period === t.key ? ui.btnActive : ui.btn}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div style={ui.group}>
          <span style={ui.label}>Custom range:</span>
          <span>From</span>
          <input
            type="datetime-local"
            step="1"
            value={fromTs ? fromTs.replace(" ", "T") : ""}
            onChange={(e) => {
              const v = e.target.value;
              setFromTs(v ? v.replace("T", " ") + (v.length === 16 ? ":00" : "") : "");
            }}
            style={{ ...ui.input, width: 200 }}
          />
          <span>To</span>
          <input
            type="datetime-local"
            step="1"
            value={toTs ? toTs.replace(" ", "T") : ""}
            onChange={(e) => {
              const v = e.target.value;
              setToTs(v ? v.replace("T", " ") + (v.length === 16 ? ":00" : "") : "");
            }}
            style={{ ...ui.input, width: 200 }}
          />
          <button onClick={applyDates} style={ui.btn}>Apply</button>
        </div>
      </div>

      {/* Row 3: Cameras */}
      <div style={{ ...ui.row, marginTop: 6 }}>
        <span style={ui.label}>Cameras:</span>
        <CameraChips
          visible={cams}
          toggle={(key) => setCams((v) => ({ ...v, [key]: !v[key] }))}
          showAll={() => setCams({ cam41: true, chel: true, pool: true })}
        />
      </div>

      {/* Vehicles / Export */}
      <div style={{ ...ui.row, marginTop: 6 }}>
        <span style={ui.label}>Vehicles</span>
        <input
          type="number"
          min={1}
          value={limitInput}
          onChange={(e) => setLimitInput(e.target.value)}
          style={{ ...ui.input, width: 90 }}
        />
        <button onClick={applyLimit} style={ui.btn}>Apply</button>
        <span style={{ fontSize: 12, opacity: 0.72 }}>
          {loading
            ? "Loading…"
            : `Showing ${rows.length} vehicles (rows ${pageFirst}-${pageLast} of ${totalGroups})`}
        </span>
        <ExportMenu
          onExport={(format) => {
            const payload = buildExportPayload(rows);
            const { filename } = payload;
            if (format === "csv") {
              downloadBlob(`${filename}.csv`, "text/csv;charset=utf-8", toCsv(payload.rows));
            } else if (format === "excel") {
              const html = toExcelHtml(payload.rows);
              const uri =
                "data:application/vnd.ms-excel;charset=utf-8," + encodeURIComponent(html);
              const a = document.createElement("a");
              a.href = uri;
              a.download = `${filename}.xls`;
              a.click();
            } else if (format === "pdf") {
              exportPdf(payload.rows);
            } else if (format === "print") {
              window.print();
            }
          }}
        />
      </div>

      {/* Images toolbar + panel */}
      {selectedPlate && latestMedia && (
        <div style={{ ...ui.row, marginTop: 10, justifyContent: "space-between" }}>
          <div style={{ fontSize: 13, opacity: 0.85 }}>
            <strong>{selectedPlate}</strong> — latest on <code>{latestMedia?.camera_id}</code>{" "}
            at {latestMedia?.time}
          </div>
          <a href={sightingsHref} style={ui.btn}>
            View all sightings for this plate
          </a>
        </div>
      )}
      <div ref={imagesRef} style={{ marginTop: 8, marginBottom: 12 }}>
        <ImagesPanel
          key={selectedPlate + ":" + (latestMedia?.detection_id || "")}
          context={
            selectedPlate && latestMedia
              ? { plate: selectedPlate, camera: latestMedia?.camera_id, time: latestMedia?.time }
              : { plate: "", camera: "", time: "" }
          }
          loading={mediaLoading}
          files={{
            vehicle: latestMedia?.files?.vehicle || null,
            background: latestMedia?.files?.background || null,
            plate: latestMedia?.files?.plate || null,
          }}
        />
      </div>

      {/* Pager */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
        <button
          onClick={() => setOffset((o) => Math.max(0, o - limit))}
          style={{
            ...ui.btn,
            height: 28,
            padding: "0 10px",
            opacity: offset <= 0 || loading ? 0.5 : 1,
            cursor: offset <= 0 || loading ? "not-allowed" : "pointer",
          }}
          disabled={offset <= 0 || loading}
        >
          ◀ Prev
        </button>
        <button
          onClick={() => setOffset((o) => Math.min(o + limit, Math.max(0, totalGroups - 1)))}
          style={{
            ...ui.btn,
            height: 28,
            padding: "0 10px",
            opacity: offset + limit >= totalGroups || loading ? 0.5 : 1,
            cursor: offset + limit >= totalGroups || loading ? "not-allowed" : "pointer",
          }}
          disabled={offset + limit >= totalGroups || loading}
        >
          Next ▶
        </button>
      </div>

      {/* Table */}
      <div
        style={{
          overflowX: "auto",
          border: "1px solid #1f2834",
          borderRadius: 12,
          background: "#0f141c",
        }}
      >
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 680 }}>
          <thead>
            <tr style={{ background: "#172131" }}>
              {["Plate", "41", "Cheltenham", "Pool", "Total"].map((h, i) => (
                <th
                  key={h}
                  style={{
                    textAlign: i === 0 ? "left" : "right",
                    padding: "10px 12px",
                    fontWeight: 700,
                    color: "#e6edf3",
                    fontSize: 14,
                    borderBottom: "1px solid #1f2834",
                    position: "sticky",
                    top: 0,
                    zIndex: 1,
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {!loading && rows.length === 0 ? (
              <tr>
                <td colSpan={5} style={{ padding: "12px", textAlign: "center", color: "#9fb4d6" }}>
                  No results
                </td>
              </tr>
            ) : (
              rows.map((r) => (
                <tr
                  key={r.plate}
                  onClick={async () => {
                    setSelectedPlate(r.plate);
                    await fetchImagesForPlate(r.plate);
                    if (imagesRef.current) {
                      imagesRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
                    }
                  }}
                  style={{
                    background: selectedPlate === r.plate ? "#15203b" : "#0f141c",
                    borderTop: "1px solid #1f2834",
                    cursor: "pointer",
                  }}
                >
                  <td
                    style={{
                      padding: "8px 12px",
                      fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                      color: "#e7f1ff",
                    }}
                  >
                    {r.plate}
                  </td>
                  <td style={{ padding: "8px 12px", textAlign: "right" }}>
                    {Number(r["41"] || 0).toLocaleString()}
                  </td>
                  <td style={{ padding: "8px 12px", textAlign: "right" }}>
                    {Number(r.cheltenham || 0).toLocaleString()}
                  </td>
                  <td style={{ padding: "8px 12px", textAlign: "right" }}>
                    {Number(r.pool || 0).toLocaleString()}
                  </td>
                  <td style={{ padding: "8px 12px", textAlign: "right" }}>
                    {Number(r.total || 0).toLocaleString()}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
